import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import logger from "logging/logger";
import { firestore } from "services/firebaseService/firebaseConfig";
import useAddEventMembersForEventIds from "modules/eventMember/hooks/useAddEventMembersForEventIds";
import { selectUser } from "modules/auth/redux/selectors";
import { selectDirectoriesOfCurrentEvent } from "modules/event/selectors";
import { useIsInEvent } from "modules/event/hooks";
import { selectCurrentEventDirectories } from "../redux/selectors";
import {
  loadCurrentDirectories,
  resetCurrentDirectories,
  updateCurrentEventDirectories,
} from "../redux/slice";
import * as EventDirectoryAPI from "../api";
import { IEventDirectoryFirestoreDoc } from "../types";
import { compareEventDirectoryWithGivenArray } from "../utils";

export type EventDirectorySnapshotListener = (
  data: IEventDirectoryFirestoreDoc[] | undefined,
) => Promise<void>;

const EVENT_DIRECTORY_COLLECTION = "EventDirectory";

export const getEventDirectoryCollection = () =>
  collection(firestore, EVENT_DIRECTORY_COLLECTION);

const useEventDirectory = () => {
  const user = useSelector(selectUser);
  const dispatch = useAppDispatch();
  const { manageEventMemberCache } = useAddEventMembersForEventIds();
  const onSnapshotHandler = useRef<EventDirectorySnapshotListener>(() =>
    Promise.resolve(),
  );
  const eventDirectories = useSelector(selectCurrentEventDirectories);
  const currentEventDirectories = useSelector(selectDirectoriesOfCurrentEvent);
  const isInEvent = useIsInEvent();
  const listener = useRef<(() => void) | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useEffect(() => unsubscribeListener, []);

  const snapshotListener = async (
    data: IEventDirectoryFirestoreDoc[] | undefined,
  ) => {
    try {
      // if there's no data in Firestore (deleted), quickly disconnect and reset
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        manageEventDirectoryCache(); // reset and unsubscribe if passed empty id

        return;
      }

      if (!eventDirectories.length || !user) {
        return;
      }
      data.forEach((doc) => {
        dispatch(updateCurrentEventDirectories(doc));
      });
      const events = data.flatMap((directory) => directory.events);
      const eventIds = events.map((event) => event.id).filter(Boolean);
      if (eventIds.length) manageEventMemberCache(eventIds as string[]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      logger.error(
        `[useEventDirectory][snapshotListener] Error occurred in snapshot listener: ${errorMessage}`,
      );
    }
  };

  onSnapshotHandler.current = snapshotListener;

  const unsubscribeListener = () => {
    if (listener.current) {
      listener.current();
      listener.current = null;
    }
  };

  const subscribeToFirestore = (directories: string[]) => {
    if (!listener.current) {
      listener.current = onSnapshot(
        query(getEventDirectoryCollection(), where("id", "in", directories)),
        (snapshot) =>
          onSnapshotHandler.current(
            snapshot.docChanges()?.map((change) => change.doc.data()) as
              | IEventDirectoryFirestoreDoc[]
              | undefined,
          ),
      );
    }
  };

  const fetchEventMembersForCurrentEvent = useCallback(
    (directories: IEventDirectoryFirestoreDoc[]) => {
      const eventIds = directories
        .flatMap((directory) => directory.events ?? [])
        .map((event) => event?.id)
        .filter(Boolean) as string[];
      manageEventMemberCache(eventIds);
    },
    [manageEventMemberCache],
  );

  const fetchEventDirectoryData = useCallback(
    async (directories: string[]) => {
      // directories found on redux is same as loaded event's
      if (
        (eventDirectories &&
          compareEventDirectoryWithGivenArray({
            eventDirectories,
            directoryIds: directories,
          })) ||
        !user
      ) {
        fetchEventMembersForCurrentEvent(eventDirectories);
        return;
      }

      const promises = directories.map((directory) =>
        EventDirectoryAPI.getEventDirectory(directory),
      );

      try {
        const responses = await Promise.all(promises);
        const filteredDirectories = responses.map(
          (response) => response.eventDirectory,
        );

        dispatch(loadCurrentDirectories(filteredDirectories));
        fetchEventMembersForCurrentEvent(filteredDirectories);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "";

        logger.error(
          `[fetchEventDirectoryData] Error occurred while fetching directory with ids ${directories}: ${errorMessage}`,
        );
      }
    },
    [dispatch, eventDirectories, fetchEventMembersForCurrentEvent, user],
  );

  useEffect(() => {
    if (currentEventDirectories?.length && isInEvent && user) {
      fetchEventDirectoryData(currentEventDirectories as string[]);
    }
  }, [currentEventDirectories, fetchEventDirectoryData, isInEvent, user]);

  const manageEventDirectoryCache = (directories?: string[]) => {
    if (directories?.length) {
      if (
        eventDirectories &&
        !compareEventDirectoryWithGivenArray({
          eventDirectories,
          directoryIds: directories,
        })
      ) {
        dispatch(resetCurrentDirectories());
      }
      fetchEventDirectoryData(directories);
      subscribeToFirestore(directories);
    } else {
      dispatch(resetCurrentDirectories());
      unsubscribeListener();
    }
  };

  return {
    eventDirectories,
    subscribeToFirestore,
    unsubscribeListener,
    fetchEventDirectoryData,
    manageEventDirectoryCache,
  };
};

export default useEventDirectory;
